/**
 * ProtectionTierBadge Component
 *
 * The single, app-wide visual for a protection tier. Five tiers (Starter < Bronze < Silver < Gold
 * < Platinum), each a medal-style pill with a consistent icon + colour. The RPO/RTO each tier means
 * is settings-managed (Backup → Settings) — this badge shows only the tier identity, so it stays
 * correct as the numbers change. Use this everywhere a tier is displayed.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201
 */

import React from 'react';

export type ProtectionTier = 'Starter' | 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
export type TierSource = 'Auto' | 'Manual';

export interface ProtectionTierBadgeProps {
  /** Tier name. Accepts any string; unknown values render a neutral pill so it never breaks. */
  tier: string | null | undefined;
  source?: TierSource;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  showLabel?: boolean;
  onClick?: () => void;
}

interface TierStyle {
  bg: string;
  text: string;
  border: string;
  icon: string;
}

/** Colour + icon per tier (low → high). Bronze/Silver/Gold keep the original medals. */
const tierConfig: Record<ProtectionTier, TierStyle> = {
  Starter: {
    bg: 'bg-slate-100 dark:bg-slate-800/50',
    text: 'text-slate-600 dark:text-slate-300',
    border: 'border-slate-300 dark:border-slate-600',
    icon: '🔰',
  },
  Bronze: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-800 dark:text-orange-300',
    border: 'border-orange-300 dark:border-orange-800',
    icon: '🥉',
  },
  Silver: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-300 dark:border-gray-600',
    icon: '🥈',
  },
  Gold: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-800 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-800',
    icon: '🥇',
  },
  Platinum: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-800 dark:text-cyan-300',
    border: 'border-cyan-300 dark:border-cyan-800',
    icon: '💎',
  },
};

/** Ordered list of the canonical tiers (low → high) for selectors. */
export const PROTECTION_TIERS: ProtectionTier[] = ['Starter', 'Bronze', 'Silver', 'Gold', 'Platinum'];

/** The medal/icon for a tier — for use in dropdown option labels so Selects match the badges. */
export const tierIcon = (tier: string | null | undefined): string =>
  (tier && (tierConfig as Record<string, TierStyle>)[tier]?.icon) || '🛡️';

const NEUTRAL: TierStyle = {
  bg: 'bg-[var(--color-bg-subtle)]',
  text: 'text-[var(--color-text-secondary)]',
  border: 'border-[var(--color-border)]',
  icon: '🛡️',
};

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

export const ProtectionTierBadge: React.FC<ProtectionTierBadgeProps> = ({
  tier,
  source,
  size = 'sm',
  showIcon = true,
  showLabel = true,
  onClick,
}) => {
  if (!tier) return <span className="text-xs text-[var(--color-text-secondary)]">—</span>;
  const config = (tierConfig as Record<string, TierStyle>)[tier] ?? NEUTRAL;

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full border ${config.bg} ${config.text} ${config.border} ${sizeClasses[size]} ${onClick ? 'cursor-pointer transition-opacity hover:opacity-80' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      title={`${tier} tier${source ? ` (${source})` : ''}`}
    >
      {showIcon && <span className="mr-1">{config.icon}</span>}
      {showLabel && tier}
      {source === 'Auto' && (
        <span className="ml-1 text-xs opacity-60" title="Auto-classified">
          ⚡
        </span>
      )}
    </span>
  );
};

/**
 * Tier Selector — card-style picker for assigning a tier (all five tiers). The RPO/RTO label per
 * tier is supplied by the caller (settings-managed) via `rpoLabel`, so it stays accurate.
 */
export interface TierSelectorProps {
  value: string;
  onChange: (tier: string) => void;
  disabled?: boolean;
  rpoLabel?: (tier: ProtectionTier) => string;
}

export const TierSelector: React.FC<TierSelectorProps> = ({ value, onChange, disabled = false, rpoLabel }) => (
  <div className="flex flex-wrap gap-2">
    {PROTECTION_TIERS.map((tier) => {
      const config = tierConfig[tier];
      const isSelected = value === tier;
      return (
        <button
          key={tier}
          type="button"
          disabled={disabled}
          onClick={() => onChange(tier)}
          className={`flex items-center rounded-lg border-2 px-3 py-2 transition-all ${
            isSelected
              ? `${config.bg} ${config.border} ${config.text} ring-2 ring-offset-1`
              : 'border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:opacity-80'
          } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
        >
          <span className="mr-2">{config.icon}</span>
          <div className="text-left">
            <div className="font-medium">{tier}</div>
            {rpoLabel && <div className="text-xs opacity-75">{rpoLabel(tier)}</div>}
          </div>
        </button>
      );
    })}
  </div>
);

export default ProtectionTierBadge;
